/**
 * UI copy for server discriminants (§12.5 rule 5).
 *
 * The server sends stable enum values; the words shown next to them are the client's business.
 * Every map is total over its enum tuple, so a new value added in `@lw/shared` is a type error
 * here rather than a blank cell on screen.
 */

import type {
  AggregateMetricKey,
  AiErrorClass,
  ContentType,
  JobStatus,
  JobType,
  MediaKind,
  MetricKey,
  PostType,
  RunStatus,
  ScheduleMode,
  ScraperBannerClass,
  ScraperKind,
  StageErrorClass,
  StageFetchStatus,
  StageMapStatus,
  TriggerSource,
  ViewMode,
  WindowKind,
} from '@lw/shared';

/** The view screen's four modes — the header tabs and the browser tab title read the same map. */
export const VIEW_MODE_LABEL: Record<ViewMode, string> = {
  metrics: 'Metrics',
  posts: 'Posts',
  feed: 'Feed',
  profile: 'Profile',
};

export const POST_TYPE_LABEL: Record<PostType, string> = {
  original: 'Original post',
  repost: 'Repost',
  repost_with_comment: 'Repost with comment',
  article: 'Article / newsletter',
  comment: 'Comment',
  other: 'Other / unknown',
};

export const CONTENT_TYPE_LABEL: Record<ContentType, string> = {
  text: 'Text only',
  image: 'Single image',
  multi_image: 'Multiple images',
  video: 'Video',
  document: 'Document / carousel',
  article_link: 'Article link',
  poll: 'Poll',
  repost: 'Reposted content',
  other: 'Other / unknown',
};

export const SCHEDULE_MODE_LABEL: Record<ScheduleMode, string> = {
  manual: 'Manual',
  daily: 'Daily',
  mon_fri: 'Mon–Fri',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  success: 'Success',
  partial: 'Partial',
  failed: 'Failed',
  mapper_error: 'Mapper error',
};

export const TRIGGER_SOURCE_LABEL: Record<TriggerSource, string> = {
  manual: 'Manual',
  own_schedule: 'Own schedule',
  group_schedule: 'Group schedule',
  catchup: 'Catch-up',
  watch: 'Watch',
};

export const STAGE_FETCH_STATUS_LABEL: Record<StageFetchStatus, string> = {
  pending: 'Pending',
  success: 'Fetched',
  failed: 'Fetch failed',
  skipped: 'Skipped',
};

export const STAGE_MAP_STATUS_LABEL: Record<StageMapStatus, string> = {
  pending: 'Pending',
  success: 'Mapped',
  failed: 'Map failed',
  skipped: 'Skipped',
};

export const STAGE_ERROR_CLASS_LABEL: Record<StageErrorClass, string> = {
  auth: 'Credentials rejected',
  quota: 'Quota exhausted',
  provider_error: 'Provider error',
  network: 'Network error',
  timeout: 'Timed out',
  mapper: 'Mapper error',
};

/**
 * Prefix of a failed #79 connection test; the backend's own message follows it.
 *
 * Its own map rather than a reuse of the scrape vocabulary above: an AI backend fails in ways a
 * scraper cannot (a rate limit, an overloaded model) and never in ways only a scraper can (a
 * mapper).
 */
export const AI_ERROR_CLASS_LABEL: Record<AiErrorClass, string> = {
  auth: 'Credentials rejected',
  rate_limit: 'Rate limited',
  overloaded: 'Provider overloaded',
  timeout: 'Timed out',
  invalid_request: 'Invalid request',
  provider_error: 'Provider error',
};

/** Headline of a per-plugin banner (§11.1); the server message follows it. */
export const SCRAPER_BANNER_LABEL: Record<ScraperBannerClass, string> = {
  auth: 'credentials rejected.',
  quota: 'quota exhausted.',
};

export const SCRAPER_KIND_LABEL: Record<ScraperKind, string> = {
  profile: 'Profile scraper',
  posts: 'Posts scraper',
  company: 'Company details scraper',
};

export const METRIC_LABEL: Record<MetricKey, string> = {
  reactions: 'Reactions',
  comments: 'Comments',
  shares: 'Shares',
  saves: 'Saves',
};

/** The four metrics plus the derived total — the five baseline rows of one author (§8.1). */
export const AGGREGATE_METRIC_LABEL: Record<AggregateMetricKey, string> = {
  ...METRIC_LABEL,
  totalEngagement: 'Total engagement',
};

export const WINDOW_KIND_LABEL: Record<WindowKind, string> = {
  trailing_90d: 'trailing 90 days',
  last_50: 'last 50 counted posts',
  insufficient: 'insufficient data',
};

export const MEDIA_KIND_LABEL: Record<MediaKind, string> = {
  image: 'Image',
  video: 'Video',
  document: 'Document',
  other: 'Media',
};

export const JOB_TYPE_LABEL: Record<JobType, string> = {
  remap: 'Re-mapping stored payloads',
  recompute_baselines: 'Recomputing baselines',
};

export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  success: 'Finished',
  failed: 'Failed',
};

/** ISO weekday (1 = Monday) → label, for the best-day callout and the day × hour matrix. */
export const WEEKDAY_LABEL: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export const WEEKDAY_SHORT: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

/** "09:00" from an hour-of-day index. */
export function hourLabel(hour: number): string {
  return `${`${hour}`.padStart(2, '0')}:00`;
}
